/**
 * EOS Scorecard Calculation Utilities
 *
 * This file contains logic for:
 * 1. Calculating metric status (on-track/off-track) based on goal orientation
 * 2. Determining trailing status (Green/Yellow/Red) based on historical data
 * 3. Formatting metric values for display
 */

import { GoalOrientation } from '../types';

export type MetricStatus = 'on-track' | 'at-risk' | 'off-track' | 'no-data';

export type TrendData = {
  value: number;
  percent: number;
  direction: 'up' | 'down' | 'flat';
};

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export type EnhancedMetricStatus = {
  status: MetricStatus;
  projectedFinal: number | null;
  timeElapsedPercent: number | null;
  expectedProgress: number | null;
  paceVariance: number | null;
  paceVariancePercent: number | null;
  trend: TrendData | null;
  confidence: ConfidenceLevel;
  message: string;
};

/**
 * Calculates the variance between actual and goal
 */
export function calculateVariance(actual: number | null, goal: number): number | null {
  if (actual === null) return null;
  return actual - goal;
}

/**
 * Calculates the percentage variance between actual and goal
 */
export function calculateVariancePercent(actual: number | null, goal: number): number | null {
  if (actual === null || goal === 0) return null;
  return ((actual - goal) / goal) * 100;
}

/**
 * Determines if a metric is on track based on its orientation and goal
 */
export function isMetricOnTrack(
  actual: number | null,
  goal: number,
  orientation: GoalOrientation = 'gte',
  rangeMin?: number | null,
  rangeMax?: number | null
): boolean | null {
  if (actual === null || actual === undefined) return null;

  switch (orientation) {
    case 'gte':
      return actual >= goal;
    case 'lte':
      return actual <= goal;
    case 'eq':
      return actual === goal;
    case 'range':
      if (rangeMin == null || rangeMax == null) return null;
      return actual >= rangeMin && actual <= rangeMax;
    default:
      return null;
  }
}

/**
 * Calculates the binary EOS status (on-track / off-track)
 */
export function calculateEOSStatus(
  actual: number | null,
  goal: number,
  orientation: GoalOrientation = 'gte',
  rangeMin?: number | null,
  rangeMax?: number | null
): MetricStatus {
  if (actual === null) return 'no-data';

  const onTrack = isMetricOnTrack(actual, goal, orientation, rangeMin, rangeMax);

  if (onTrack === null) return 'no-data';
  return onTrack ? 'on-track' : 'off-track';
}

/**
 * Calculates the trailing status (Red/Yellow/Green) based on the last 3 data points
 * 
 * Rules:
 * - Green: On track for all valid recent weeks (up to 3)
 * - Red: Off track for all valid recent weeks
 * - Yellow: Mixed results (at least one off-track)
 */
export function calculateTrailingStatus(
  weeklyStatuses: (boolean | null)[],
  lookbackWeeks: number = 3
): 'green' | 'yellow' | 'red' | 'no-data' {
  // Filter out weeks with no data first to find the last N weeks WITH data
  const validStatuses = weeklyStatuses.filter(s => s !== null) as boolean[];

  // Get the last N valid weeks
  const recentStatuses = validStatuses.slice(-lookbackWeeks);

  if (recentStatuses.length === 0) return 'no-data';

  const onTrackCount = recentStatuses.filter(s => s === true).length;
  const offTrackCount = recentStatuses.filter(s => s === false).length;

  // If all valid data points are off track -> RED
  if (offTrackCount === recentStatuses.length) return 'red';
  
  // If all valid data points are on track -> GREEN
  if (onTrackCount === recentStatuses.length) return 'green';
  
  // Mixed results -> YELLOW
  return 'yellow';
}

/**
 * Returns the symbol for the goal orientation (≥, ≤, =, Range)
 */
export function getOrientationSymbol(
  orientation: GoalOrientation, 
  rangeMin?: number | null, 
  rangeMax?: number | null
): string {
  switch (orientation) {
    case 'gte':
      return '≥';
    case 'lte':
      return '≤';
    case 'eq':
      return '=';
    case 'range':
      if (rangeMin != null && rangeMax != null) {
        return `${rangeMin}-${rangeMax}`;
      }
      return 'Range';
    default:
      return '≥';
  }
}

/**
 * Formats a metric value based on its measurement type
 */
export function formatMetricValue(
  value: number | null | undefined, 
  type: 'currency' | 'count' | 'percentage' | string,
  unit?: string
): string {
  if (value === null || value === undefined) return '--';

  switch (type) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);

    case 'percentage':
      return `${value.toFixed(1)}%`;

    case 'count':
    default:
      if (unit && unit !== 'number') {
         // Custom unit handling could go here if needed
         return `${value.toLocaleString('en-US')} ${unit}`;
      }
      return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  }
}

// ============================================================================
// Date & Period Utilities
// ============================================================================

export function getCurrentWeekNumber(): number {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const diffTime = now.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return Math.ceil((diffDays + startOfYear.getDay() + 1) / 7);
}

export function getWeekDateRange(weekNumber: number, year: number): { start: Date; end: Date } {
  const start = new Date(year, 0, 1);
  const daysToAdd = (weekNumber - 1) * 7;
  start.setDate(start.getDate() + daysToAdd);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  return { start, end };
}

export function formatDateRange(start: Date, end: Date): string {
  const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', formatOptions)} - ${end.toLocaleDateString('en-US', formatOptions)}`;
}

export interface WeekColumn {
  weekNumber: number;
  label: string;
  startDate: Date;
  endDate: Date;
  isReviewWeek: boolean;
}

export function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

export function getStartOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

export function addWeeks(date: Date, weeks: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Returns true when `weekStart` is the start of the most recently COMPLETED ISO
 * week (i.e., last week relative to today). The L10 scorecard reviews the prior
 * week's results, so this is the week that should be highlighted in the grid.
 */
export function isReviewWeek(weekStart: Date): boolean {
  const now = new Date();
  const reviewWeekStart = addWeeks(getStartOfISOWeek(now), -1);
  return weekStart.toDateString() === reviewWeekStart.toDateString();
}

export function getCurrentQuarter(): number {
  const month = new Date().getMonth();
  return Math.floor(month / 3) + 1;
}

export function getCurrentYear(): number {
  return new Date().getFullYear();
}

export function getQuarterWeeks(quarter: number, year: number): WeekColumn[] {
  const weeks: WeekColumn[] = [];
  const quarterStartMonth = (quarter - 1) * 3;
  const quarterStart = new Date(year, quarterStartMonth, 1);
  
  // Align to Monday start
  const weekStart = getStartOfISOWeek(quarterStart);

  for (let i = 0; i < 13; i++) {
    const currentWeekStart = addWeeks(weekStart, i);
    const currentWeekEnd = addDays(currentWeekStart, 6);
    const weekNum = getISOWeek(currentWeekStart);

    const startMonth = currentWeekStart.toLocaleDateString('en-US', { month: 'short' });
    const startDay = currentWeekStart.getDate();
    const endDay = currentWeekEnd.getDate();

    weeks.push({
      weekNumber: weekNum,
      label: `${startMonth} ${startDay}-${endDay}`,
      startDate: currentWeekStart,
      endDate: currentWeekEnd,
      isReviewWeek: isReviewWeek(currentWeekStart)
    });
  }

  return weeks;
}
